<template>

  <div class="wa-voice-wrap">

    <div

      class="wa-voice"

      :class="sent ? 'wa-voice--out' : 'wa-voice--in'"

    >

      <button

        type="button"

        class="wa-voice__play"

        :disabled="!mediaUrl || failed"

        :aria-label="isPlaying ? pauseLabel : playLabel"

        @click="togglePlayback"

      >

        <svg v-if="isPlaying" class="wa-voice__play-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">

          <path d="M6 5h4v14H6V5zm8 0h4v14h-4V5z" />

        </svg>

        <svg v-else class="wa-voice__play-icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">

          <path d="M8 5v14l11-7L8 5z" />

        </svg>

      </button>



      <div

        ref="waveEl"

        class="wa-voice__wave min-w-0 flex-1"

        role="presentation"

        @click="seekFromClick"

      >

        <div

          v-for="(height, index) in waveformBars"

          :key="index"

          class="wa-voice__bar"

          :class="{ 'wa-voice__bar--played': index <= playedBarIndex }"

          :style="{ height: `${height}%` }"

        />

        <span

          class="wa-voice__scrubber"

          :style="scrubberStyle"

          aria-hidden="true"

        />

      </div>



      <audio

        v-if="mediaUrl"

        ref="audioEl"

        :src="mediaUrl"

        preload="metadata"

        class="wa-voice__audio-native"

        @loadedmetadata="onLoaded"

        @ended="onEnded"

        @pause="onPause"

        @play="onPlay"

        @error="onError"

      />

    </div>



    <div class="wa-bubble__footer wa-bubble__footer--voice">

      <span class="wa-voice__time">{{ displayTime }}</span>

      <div class="wa-bubble__footer-end">

        <span class="wa-bubble__time">{{ timestampLabel }}</span>

        <span

          v-if="sent && status"

          class="wa-bubble__delivery"

          :class="{

            'wa-bubble__delivery--failed': status === 'FAILED',

            'wa-bubble__delivery--pending': status === 'PENDING'

          }"

          :title="deliveryTitle"

        >{{ deliveryIcon }}</span>

      </div>

    </div>

  </div>

</template>



<script setup>

import { computed, onBeforeUnmount, ref, watch } from 'vue'

import { useI18n } from 'vue-i18n'



const props = defineProps({

  mediaUrl: { type: String, default: '' },

  durationSeconds: { type: Number, default: null },

  sent: { type: Boolean, default: false },

  messageId: { type: [Number, String], default: null },

  timestampLabel: { type: String, default: '' },

  status: { type: String, default: '' },

  deliveryIcon: { type: String, default: '' },

  deliveryTitle: { type: String, default: '' }

})



const emit = defineEmits(['error', 'ready'])



const { t } = useI18n()



const audioEl = ref(null)

const waveEl = ref(null)

const isPlaying = ref(false)

const failed = ref(false)

const currentTime = ref(0)

const audioDuration = ref(0)



const BAR_COUNT = 36



const playLabel = computed(() => t('kanban.leadDetail.whatsapp.voicePlay', 'Reproduzir'))

const pauseLabel = computed(() => t('kanban.leadDetail.whatsapp.voicePause', 'Pausar'))



function seedFromMessageId() {

  const raw = String(props.messageId ?? props.mediaUrl ?? '0')

  let hash = 0

  for (let i = 0; i < raw.length; i++) {

    hash = (hash * 31 + raw.charCodeAt(i)) >>> 0

  }

  return hash || 1

}



const waveformBars = computed(() => {

  let seed = seedFromMessageId()

  const bars = []

  for (let i = 0; i < BAR_COUNT; i++) {

    seed = (seed * 1664525 + 1013904223) >>> 0

    const normalized = (seed % 100) / 100

    const height = 30 + normalized * 70

    bars.push(Math.round(height))

  }

  return bars

})



const totalDuration = computed(() => {

  if (audioDuration.value > 0) return audioDuration.value

  if (props.durationSeconds > 0) return props.durationSeconds

  return 0

})



const progressRatio = computed(() => {

  if (!totalDuration.value) return 0

  return Math.min(1, currentTime.value / totalDuration.value)

})



const scrubberStyle = computed(() => {

  const trackWidth = waveEl.value?.clientWidth ?? 0

  if (trackWidth > 0) {

    const x = progressRatio.value * trackWidth

    return { transform: `translate3d(${x}px, -50%, 0)` }

  }

  return { left: `${progressRatio.value * 100}%`, transform: 'translate3d(-50%, -50%, 0)' }

})



const playedBarIndex = computed(() => {

  if (!totalDuration.value) return -1

  return Math.ceil(progressRatio.value * BAR_COUNT) - 1

})



const displayTime = computed(() => {

  const seconds = isPlaying.value || currentTime.value > 0

    ? Math.floor(currentTime.value)

    : Math.floor(totalDuration.value)

  if (!seconds || seconds < 0) return '0:00'

  const mins = Math.floor(seconds / 60)

  const secs = seconds % 60

  return `${mins}:${String(secs).padStart(2, '0')}`

})



let activeAudio = null

let progressRafId = null



function cancelProgressLoop() {

  if (progressRafId !== null) {

    cancelAnimationFrame(progressRafId)

    progressRafId = null

  }

}



function syncPlaybackProgress() {

  const el = audioEl.value

  if (!el || el.paused || el.ended) {

    cancelProgressLoop()

    return

  }

  currentTime.value = el.currentTime

  progressRafId = requestAnimationFrame(syncPlaybackProgress)

}



function startProgressLoop() {

  cancelProgressLoop()

  progressRafId = requestAnimationFrame(syncPlaybackProgress)

}



function pauseOthers() {

  if (activeAudio && activeAudio !== audioEl.value) {

    activeAudio.pause()

  }

}



function togglePlayback() {

  const el = audioEl.value

  if (!el || failed.value) return

  if (isPlaying.value) {

    el.pause()

    return

  }

  pauseOthers()

  activeAudio = el

  el.play().catch(() => {

    failed.value = true

    emit('error')

  })

}



function seekFromClick(event) {

  const el = audioEl.value

  if (!el || !totalDuration.value) return

  const rect = event.currentTarget.getBoundingClientRect()

  const ratio = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width))

  el.currentTime = ratio * totalDuration.value

  currentTime.value = el.currentTime

}



function onLoaded() {

  failed.value = false

  if (audioEl.value?.duration && Number.isFinite(audioEl.value.duration)) {

    audioDuration.value = audioEl.value.duration

  }

  emit('ready')

}



function onPlay() {

  isPlaying.value = true

  startProgressLoop()

}



function onPause() {

  isPlaying.value = false

  cancelProgressLoop()

  if (audioEl.value) currentTime.value = audioEl.value.currentTime

}



function onEnded() {

  isPlaying.value = false

  cancelProgressLoop()

  currentTime.value = 0

  if (audioEl.value) audioEl.value.currentTime = 0

}



function onError() {

  failed.value = true

  isPlaying.value = false

  emit('error')

}



watch(() => props.mediaUrl, () => {

  failed.value = false

  currentTime.value = 0

  audioDuration.value = 0

  isPlaying.value = false

})



onBeforeUnmount(() => {

  cancelProgressLoop()

  if (activeAudio === audioEl.value) activeAudio = null

  audioEl.value?.pause()

})

</script>


